import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './HomePage.css';

// Sticky büyük görsel (scroll-sticky-img-wrap) — ~550-600px gösteriliyor
const toCloudinaryMain = (url) => {
  if (!url || !url.includes('cloudinary.com')) return url;
  return url.replace('/upload/', '/upload/w_700/h_700/c_pad/b_rgb:f5f5f5/q_auto:good/f_auto/');
};

// Öne çıkan ürün kartları (products-grid) — ~330px gösteriliyor
const toCloudinaryCard = (url) => {
  if (!url || !url.includes('cloudinary.com')) return url;
  return url.replace('/upload/', '/upload/w_450/h_450/c_pad/b_rgb:f5f5f5/q_auto:good/f_auto/');
};

const toCloudinaryThumb = (url) => {
  if (!url || !url.includes('cloudinary.com')) return url;
  return url.replace('/upload/', '/upload/w_200/h_200/c_pad/b_rgb:f5f5f5/q_auto:good/f_auto/');
};

// İndirimli fiyat gösterimi — eski fiyat üstü çizili + yeni fiyat + yüzde fark, rozet yok
function PriceDisplay({ product, className }) {
  const price = Number(product.price);
  const compareAt = product.compare_at_price ? Number(product.compare_at_price) : null;
  if (!compareAt || compareAt <= price) {
    return <span className={className}>₺{price.toFixed(2)}</span>;
  }
  const percentOff = Math.round((1 - price / compareAt) * 100);
  return (
    <span className={className}>
      <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '0.85em', marginRight: '6px' }}>
        ₺{compareAt.toFixed(2)}
      </span>
      <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>₺{price.toFixed(2)}</span>
      <span style={{ color: '#e74c3c', fontSize: '0.85em', marginLeft: '4px' }}>(-%{percentOff})</span>
    </span>
  );
}

function HomePage() {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [activeScrollIndex, setActiveScrollIndex] = useState(0);
  const [bannerHovered, setBannerHovered] = useState(false);
  const [hoveredImageIndex, setHoveredImageIndex] = useState({});
  const scrollSectionRef = useRef(null);
  const scrollCountRef = useRef(0);

  // Hero Banner Slider Verileri — Cloudinary üzerinden, srcset ile ekran genişliğine göre doğru boyut seçilir
  const cloudinarySrcSet = (publicId) =>
    [480, 768, 1200, 1920]
      .map(w => `https://res.cloudinary.com/dgnhcvmvi/image/upload/w_${w},q_auto:good,f_auto/v1783943458/${publicId} ${w}w`)
      .join(', ');

  const slides = [
    { id: 1, image: 'https://res.cloudinary.com/dgnhcvmvi/image/upload/w_1920,q_auto:good,f_auto/v1783943458/fular_gqldtc.jpg', srcSet: cloudinarySrcSet('fular_gqldtc.jpg'), alt: 'Fularlar', link: '/urunler?kategori=Fular' },
    { id: 2, image: 'https://res.cloudinary.com/dgnhcvmvi/image/upload/w_1920,q_auto:good,f_auto/v1783943458/e%C5%9Farp_wzhtg3.jpg', srcSet: cloudinarySrcSet('e%C5%9Farp_wzhtg3.jpg'), alt: 'Eşarplar', link: '/urunler?kategori=E%C5%9Farp' },
    { id: 3, image: 'https://res.cloudinary.com/dgnhcvmvi/image/upload/w_1920,q_auto:good,f_auto/v1783943458/%C5%9Fal_ozklgh.jpg', srcSet: cloudinarySrcSet('%C5%9Fal_ozklgh.jpg'), alt: 'Şallar', link: '/urunler?kategori=%C5%9Eal' },
  ];

  // Slider otomatik geçiş
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [slides.length]);

  // Ürünleri yükle
  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  // Scroll kitleme: section odakta iken wheel/touch ile ürün geç
  useEffect(() => {
    if (featuredProducts.length === 0) return;
    const totalItems = Math.min(featuredProducts.length, 3);

    let cooldown = false;
    const COOLDOWN_MS = 550;

    const isSectionInView = () => {
      const section = scrollSectionRef.current;
      if (!section) return false;
      const rect = section.getBoundingClientRect();
      const centerY = rect.top + rect.height / 2;
      return centerY > 0 && centerY < window.innerHeight;
    };

    // --- Wheel (masaüstü) ---
    const handleWheel = (e) => {
      if (!isSectionInView()) return;
      const dir = e.deltaY > 0 ? 1 : -1;
      const next = scrollCountRef.current + dir;
      // Sadece geçiş yapılabilecekse yakala; sınırdaysa serbest bırak
      if (next >= 0 && next < totalItems) {
        e.preventDefault();
        if (cooldown) return;
        cooldown = true;
        setTimeout(() => { cooldown = false; }, COOLDOWN_MS);
        scrollCountRef.current = next;
        setActiveScrollIndex(next);
      }
    };

    // --- Touch (mobil) ---
    let touchStartY = 0;

    const handleTouchStart = (e) => {
      touchStartY = isSectionInView() ? e.touches[0].clientY : 0;
    };

    const handleTouchEnd = (e) => {
      if (touchStartY === 0) return;
      const deltaY = touchStartY - e.changedTouches[0].clientY;
      touchStartY = 0;
      if (Math.abs(deltaY) < 30) return;
      const dir = deltaY > 0 ? 1 : -1;
      const next = scrollCountRef.current + dir;
      if (next >= 0 && next < totalItems) {
        e.preventDefault();
        if (cooldown) return;
        cooldown = true;
        setTimeout(() => { cooldown = false; }, COOLDOWN_MS);
        scrollCountRef.current = next;
        setActiveScrollIndex(next);
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: false });
    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [featuredProducts]);


  const fetchFeaturedProducts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/products`);
      if (response.ok) {
        const data = await response.json();
        const shuffled = [...data].sort(() => Math.random() - 0.5);
        setFeaturedProducts(shuffled.slice(0, 6));
      } else {
        setFeaturedProducts([]);
      }
    } catch (error) {
      setFeaturedProducts([]);
    }
  };

  const getProductImages = (product) => {
    if (!product.images) return [];

    if (typeof product.images === 'string') {
      try {
        const parsed = JSON.parse(product.images);
        if (Array.isArray(parsed)) return parsed;
        // {"0": "url", "1": "url"} gibi object ise values al
        if (parsed && typeof parsed === 'object') return Object.values(parsed).filter(url => url);
        return [];
      } catch {
        return product.images.split(',').map(url => url.trim()).filter(url => url);
      }
    }

    return Array.isArray(product.images) ? product.images : [];
  };

  const scrollProducts = featuredProducts.slice(0, 3);

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'İpek Aksesuar',
    url: 'https://ipekaksesuar.com',
    description: 'El işçiliği ile üretilmiş premium kalite ipek eşarplar, şallar ve aksesuarlar.',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      availableLanguage: 'Turkish'
    }
  };

  return (
    <>
    <Helmet>
      <title>İpek Aksesuar | Premium İpek Eşarp ve Şal</title>
      <meta name="description" content="El işçiliği ile üretilmiş premium kalite ipek eşarplar, şallar ve aksesuarlar. Özel hediye kutusu ile gönderim." />
      <link rel="canonical" href="https://ipekaksesuar.com" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="İpek Aksesuar | Premium İpek Eşarp ve Şal" />
      <meta property="og:description" content="El işçiliği ile üretilmiş premium kalite ipek eşarplar, şallar ve aksesuarlar. Özel hediye kutusu ile gönderim." />
      <meta property="og:url" content="https://ipekaksesuar.com" />
      <meta property="og:site_name" content="İpek Aksesuar" />
      <meta property="og:locale" content="tr_TR" />
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
    </Helmet>
    <div className="homepage-container">

      {/*
      <div className="annelergunu-slider"
      onClick={() => navigate(`/urunler`)}>
        <img src='/anne_slider.png' className="annelergunu-img" alt="Anneler Günü" />
      </div>
      */}
      {/* Hero Banner Slider */}
      <section className="hero-slider">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`hero-slide ${currentSlide === index ? 'active' : ''}`}
            onClick={() => currentSlide === index && navigate(slide.link)}
            style={{ cursor: 'pointer', pointerEvents: currentSlide === index ? 'auto' : 'none' }}
          >
            <img
              src={slide.image}
              srcSet={slide.srcSet}
              sizes="100vw"
              alt={slide.alt}
              className="hero-slide-img"
              width="1920"
              height="700"
              fetchPriority={index === 0 ? 'high' : undefined}
              loading={index === 0 ? 'eager' : 'lazy'}
            />
          </div>
        ))}

        <div className="slider-dots">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={(e) => { e.stopPropagation(); setCurrentSlide(index); }}
              className={`slider-dot ${currentSlide === index ? 'active' : ''}`}
            />
          ))}
        </div>
      </section>

      {/* Scroll Sticky Ürün Bölümü — section her zaman DOM'da kalır (height: 100vh CSS'te sabit),
          böylece ürünler API'den gecikmeli gelse bile CLS oluşmaz */}
      <section className="scroll-section" ref={scrollSectionRef} style={{ visibility: scrollProducts.length > 0 ? 'visible' : 'hidden' }}>
        {scrollProducts.length > 0 && (() => {
          return (
            <>
            <div className="scroll-section-inner">
              <h2 className="scroll-section-title">Koleksiyondan Seçmeler</h2>
              <div className="scroll-section-layout">

                {/* Sol: Sticky Görsel */}
                <div className="scroll-sticky-col">
                  <div className="scroll-sticky-img-wrap">
                    {(() => {
                      const active = scrollProducts[activeScrollIndex];
                      if (!active) return null;
                      const imgs = getProductImages(active);
                      return imgs.length > 0 ? (
                        <img
                          key={active.id}
                          src={toCloudinaryMain(imgs[1] || imgs[0])}
                          alt={active.name}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <div style={{ fontSize: '80px' }}>🧣</div>
                      );
                    })()}
                  </div>
                  {/* Aktif ürün bilgisi */}
                  {(() => {
                    const active = scrollProducts[activeScrollIndex];
                    if (!active) return null;
                    return (
                      <div className="scroll-sticky-info">
                        <h3>{active.name}</h3>
                        <p><PriceDisplay product={active} /></p>
                        <button
                          className="scroll-sticky-btn"
                          onClick={() => navigate(`/urun/${active.slug || active.id}`)}
                        >
                          Ürünü İncele →
                        </button>
                      </div>
                    );
                  })()}
                </div>

                {/* Sağ: Ürün Kartları */}
                <div className="scroll-cards-col">
                  {scrollProducts.map((product, index) => {
                    const imgs = getProductImages(product);
                    const isActive = activeScrollIndex === index;
                    return (
                      <div
                        key={product.id}
                        className={`scroll-card${isActive ? ' active' : ''}`}
                        onClick={() => navigate(`/urun/${product.slug || product.id}`)}
                      >
                        {/* Küçük resim */}
                        <div className="scroll-card-thumb">
                          {imgs.length > 0 ? (
                            <img src={toCloudinaryThumb(imgs[1] || imgs[0])} alt={product.name}
                              loading="lazy"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <div className="scroll-card-thumb-placeholder">🧣</div>
                          )}
                        </div>
                        {/* Yazılar */}
                        <div className="scroll-card-body">
                          <h3 className="scroll-card-name">{product.name}</h3>
                          <p className="scroll-card-price"><PriceDisplay product={product} /></p>
                        </div>
                        {/* Ok işareti */}
                        <span className="scroll-card-arrow">→</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            </>
          );
        })()}
      </section>

      {/* Tam Genişlik Tıklanabilir Banner */}
      <div
        onClick={() => navigate('/urunler')}
        onMouseEnter={() => setBannerHovered(true)}
        onMouseLeave={() => setBannerHovered(false)}
        style={{
          width: '100%',
          height: '700px',
          overflow: 'hidden',
          cursor: 'pointer',
          position: 'relative'
        }}
      >
        {/* Fotoğraf */}
        <img
          src="https://i.imgur.com/4Kd3ZQO.jpeg"
          alt="Koleksiyon Banner"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            transition: 'transform 0.5s ease',
            transform: bannerHovered ? 'scale(1.04)' : 'scale(1)'
          }}
        />
        {/* Koyu filtre */}
        <div
          className="banner-dark-filter"
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.3)',
            opacity: bannerHovered ? 1 : 0,
            transition: 'opacity 0.4s ease'
          }}
        />
        {/* Banner yazısı */}
        <div
          className="banner-overlay-text"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            textAlign: 'center',
            opacity: bannerHovered ? 1 : 0,
            transition: 'opacity 0.4s ease'
          }}
        >
          <p style={{
            fontSize: '14px',
            fontWeight: '600',
            letterSpacing: '3px',
            textTransform: 'uppercase',
            marginBottom: '12px',
            opacity: 0.85
          }}>
            Koleksiyonu Keşfet
          </p>
          <h2 style={{
            fontSize: '36px',
            fontWeight: '700',
            letterSpacing: '-0.5px'
          }}>
            Tüm Ürünleri Gör →
          </h2>
        </div>
      </div>

      {/* Öne Çıkan Ürünler */}
      <section className="featured-section">
        <h2 className="section-title">Öne Çıkan Ürünler</h2>
        <div className="products-grid">
          {featuredProducts.slice(0, 4).map(product => {
            const productImages = getProductImages(product);
            const currentImgIdx = hoveredImageIndex[product.id] ?? 0;

            return (
              <div
                key={product.id}
                className="product-card"
                onClick={() => navigate(`/urun/${product.slug || product.id}`)}
                onMouseMove={(e) => {
                  if (productImages.length <= 1) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const idx = Math.floor((x / rect.width) * productImages.length);
                  const clamped = Math.max(0, Math.min(idx, productImages.length - 1));
                  setHoveredImageIndex(prev => ({ ...prev, [product.id]: clamped }));
                }}
                onMouseLeave={() => {
                  setHoveredImageIndex(prev => ({ ...prev, [product.id]: 0 }));
                }}
                onTouchStart={(e) => {
                  if (productImages.length <= 1) return;
                  e.currentTarget._touchStartX = e.touches[0].clientX;
                }}
                onTouchEnd={(e) => {
                  if (productImages.length <= 1) return;
                  const startX = e.currentTarget._touchStartX;
                  if (startX == null) return;
                  const deltaX = startX - e.changedTouches[0].clientX;
                  if (Math.abs(deltaX) < 20) return;
                  const dir = deltaX > 0 ? 1 : -1;
                  setHoveredImageIndex(prev => {
                    const cur = prev[product.id] ?? 0;
                    const next = Math.max(0, Math.min(cur + dir, productImages.length - 1));
                    return { ...prev, [product.id]: next };
                  });
                }}
              >
                {/* Fotoğraf alanı */}
                <div className="product-img-wrap">
                  {productImages.length > 0 ? (
                    <img
                      src={toCloudinaryCard(productImages[currentImgIdx])}
                      alt={product.name}
                      className="product-img"
                      loading="lazy"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="product-img-placeholder">🧣</div>
                  )}

                  {/* İnce çubuk göstergesi — birden fazla fotoğraf varsa */}
                  {productImages.length > 1 && (
                    <div className="img-progress-bar">
                      {productImages.map((_, i) => (
                        <div
                          key={i}
                          className={`img-progress-seg${i === currentImgIdx ? ' active' : ''}`}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Alt bilgi */}
                <div className="product-meta">
                  <span className="product-meta-name">{product.name}</span>
                  <span className="product-meta-price"><PriceDisplay product={product} /></span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Kampanya Banner */}
      <section className="campaign-banner">
        <div className="campaign-content">
          <h2 className="campaign-title">
            Yaz indirimleri Başladı!
          </h2>
          <p className="campaign-description">
            Yaz sonuna özel indirim fırsatlarını kaçırmayın!{' '}
            <span className="campaign-code">%20 indirim</span>
          </p>
          <button
            onClick={() => navigate('/kayit')}
            className="campaign-button"
          >
            Hemen Üye Ol
          </button>
        </div>
      </section>
    </div>
    </>
  );
}

export default HomePage;
